import { supabase } from './supabase';

/**
 * Note interface
 */
export interface Note {
  id: number;
  title: string | null;
  content: any[]; // Plate.js content is an array of JSON objects
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  project_id?: number | string;
}

/**
 * NoteFolder interface
 */
export interface NoteFolder {
  id: number;
  name: string;
  description: string | null;
  parent_folder_id: number | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  project_id?: number | string;
}

/**
 * Entity-Note relationship interface
 */
export interface EntityNote {
  id: number;
  note_id: number;
  entity_id: number;
  created_at: string;
  note?: Note;
}

/**
 * Folder-Note relationship interface
 */
export interface FolderNote {
  id: number;
  note_id: number;
  folder_id: number;
  position: number;
  created_at: string;
  note?: Note;
}

/**
 * Folder with hierarchical structure interface
 */
export interface FolderWithChildren extends NoteFolder {
  children?: FolderWithChildren[];
}

/**
 * Get all notes
 * @returns Promise with array of notes
 */
export const getAllNotes = async (): Promise<Note[]> => {
  const { data, error } = await supabase
    .from('notes')
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }

  return data;
};

/**
 * Get note by ID
 * @param noteId - Note ID
 * @returns Promise with note
 */
export const getNoteById = async (noteId: string | number): Promise<Note> => {
  const { data, error } = await supabase
    .from('notes')
    .select('*')
    .eq('id', noteId)
    .single();

  if (error) {
    console.error('Error fetching note:', error);
    throw error;
  }

  return data;
};

/**
 * Create a new note
 * @param title - Note title (optional)
 * @param content - Note content (Plate.js content)
 * @param isPinned - Whether note is pinned (default: false)
 * @returns Promise with created note
 */
export const createNote = async (
  title: string | null = null,
  content: any[] = [],
  isPinned: boolean = false
): Promise<Note> => {
  const { data, error } = await supabase
    .from('notes')
    .insert([
      {
        title,
        content,
        is_pinned: isPinned,
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating note:', error);
    throw error;
  }

  return data;
};

/**
 * Update a note
 * @param noteId - Note ID
 * @param data - Updated note data
 * @returns Promise with updated note
 */
export const updateNote = async (
  noteId: number,
  data: Partial<Note>
): Promise<Note> => {
  const { data: updatedNote, error } = await supabase
    .from('notes')
    .update(data)
    .eq('id', noteId)
    .select()
    .single();

  if (error) {
    console.error('Error updating note:', error);
    throw error;
  }

  return updatedNote;
};

/**
 * Delete a note
 * @param noteId - Note ID
 */
export const deleteNote = async (noteId: number): Promise<void> => {
  const { error } = await supabase
    .from('notes')
    .delete()
    .eq('id', noteId);

  if (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};

/**
 * Attach a note to an entity
 * @param noteId - Note ID
 * @param entityId - Entity ID
 * @returns Promise with created entity-note relationship
 */
export const attachNoteToEntity = async (
  noteId: number,
  entityId: number
): Promise<EntityNote> => {
  const { data, error } = await supabase
    .from('entity_notes')
    .insert([
      {
        note_id: noteId,
        entity_id: entityId,
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error attaching note to entity:', error);
    throw error;
  }

  return data;
};

/**
 * Detach a note from an entity
 * @param noteId - Note ID
 * @param entityId - Entity ID
 */
export const detachNoteFromEntity = async (
  noteId: number,
  entityId: number
): Promise<void> => {
  const { error } = await supabase
    .from('entity_notes')
    .delete()
    .eq('note_id', noteId)
    .eq('entity_id', entityId);

  if (error) {
    console.error('Error detaching note from entity:', error);
    throw error;
  }
};

/**
 * Get all notes for an entity
 * @param entityId - Entity ID
 * @returns Promise with array of notes associated with the entity
 */
export const getEntityNotes = async (entityId: number | string): Promise<Note[]> => {
  const { data, error } = await supabase
    .from('entity_notes')
    .select(`
      id,
      note_id,
      entity_id,
      created_at,
      note:note_id (*)
    `)
    .eq('entity_id', entityId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching entity notes:', error);
    throw error;
  }

  // Extract the notes from the relationships
  const notes: Note[] = [];

  for (const item of data) {
    if (item.note && typeof item.note === 'object') {
      // Convert to unknown first, then to Note
      const noteData = item.note as unknown;
      notes.push(noteData as Note);
    } else {
      console.error('Expected note object not found in entity_notes response');
    }
  }

  return notes;
};

/**
 * Get or create a note for an entity
 * This is for backward compatibility with the existing entity notes system
 * @param entityId - Entity ID
 * @returns Promise with a note
 */
export const getOrCreateEntityNote = async (entityId: number | string): Promise<Note> => {
  // Try to get existing notes for the entity
  const notes = await getEntityNotes(entityId);

  // If there's at least one note, return the first one
  if (notes && notes.length > 0) {
    return notes[0];
  }

  // If no notes exist for this entity, create a new one
  const newNote = await createNote(`Entity ${entityId} Notes`, []);

  // Attach the new note to the entity
  await attachNoteToEntity(newNote.id, Number(entityId));

  return newNote;
};

/**
 * Create a note folder
 * @param name - Folder name
 * @param description - Folder description (optional)
 * @param parentFolderId - Parent folder ID (optional)
 * @returns Promise with created folder
 */
export const createNoteFolder = async (
  name: string,
  description: string | null = null,
  parentFolderId: number | null = null
): Promise<NoteFolder> => {
  console.log('Creating folder with params:', { name, description, parentFolderId });

  // Get the user's first tenant ID
  try {
    // First try to get the current session
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      console.error('Error getting auth session:', sessionError);
      // Continue and try to get tenant ID directly
    } else {
      console.log('Current session:', sessionData?.session?.user);
    }

    // Get the first tenant ID associated with the user
    const { data: tenantData, error: tenantError } = await supabase
      .from('user_tenants')
      .select('tenant_id')
      .limit(1)
      .single();

    if (tenantError) {
      console.error('Error getting tenant ID:', tenantError);
      throw tenantError;
    }

    console.log('Using tenant ID:', tenantData.tenant_id);

    // Create the note folder with explicit tenant_id
    const { data, error } = await supabase
      .from('note_folders')
      .insert([
        {
          name,
          description,
          parent_folder_id: parentFolderId,
          tenant_id: tenantData.tenant_id, // Explicitly set the tenant_id
        }
      ])
      .select()
      .single();

    if (error) {
      console.error('Error creating note folder:', error);
      throw error;
    }


    return data;
  } catch (error) {
    console.error('Error in createNoteFolder:', error);
    throw error;
  }
};

/**
 * Update a note folder
 * @param folderId - Folder ID
 * @param data - Updated folder data
 * @returns Promise with updated folder
 */
export const updateNoteFolder = async (
  folderId: number,
  data: Partial<NoteFolder>
): Promise<NoteFolder> => {
  const { data: updatedFolder, error } = await supabase
    .from('note_folders')
    .update(data)
    .eq('id', folderId)
    .select()
    .single();

  if (error) {
    console.error('Error updating note folder:', error);
    throw error;
  }


  return updatedFolder;
};

/**
 * Delete a note folder
 * @param folderId - Folder ID
 */
export const deleteNoteFolder = async (folderId: number): Promise<void> => {
  const { error } = await supabase
    .from('note_folders')
    .delete()
    .eq('id', folderId);

  if (error) {
    console.error('Error deleting note folder:', error);
    throw error;
  }

};

/**
 * Get all note folders
 * @returns Promise with array of folders
 */
export const getAllNoteFolders = async (): Promise<NoteFolder[]> => {
  const { data, error } = await supabase
    .from('note_folders')
    .select('*')
    .order('name');

  if (error) {
    console.error('Error fetching note folders:', error);
    throw error;
  }

  return data;
};

/**
 * Get hierarchical folder structure
 * @returns Promise with array of folders with nested children
 */
export const getFolderStructure = async (): Promise<FolderWithChildren[]> => {
  // Fetch all folders
  const { data, error } = await supabase
    .from('note_folders')
    .select('*')
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching folder structure:', error);
    throw error;
  }

  // Convert to nested structure
  const folderMap = new Map<number, FolderWithChildren>();
  const rootFolders: FolderWithChildren[] = [];

  // First, create all folder objects
  data.forEach((folder: NoteFolder) => {
    folderMap.set(folder.id, { ...folder, children: [] });
  });

  // Then, establish parent-child relationships
  data.forEach((folder: NoteFolder) => {
    const folderWithChildren = folderMap.get(folder.id);

    if (folder.parent_folder_id && folderMap.has(folder.parent_folder_id)) {
      // This is a child folder
      const parentFolder = folderMap.get(folder.parent_folder_id);
      if (parentFolder && parentFolder.children) {
        parentFolder.children.push(folderWithChildren as FolderWithChildren);
      }
    } else {
      // This is a root folder
      rootFolders.push(folderWithChildren as FolderWithChildren);
    }
  });

  return rootFolders;
};

/**
 * Add a note to a folder
 * @param noteId - Note ID
 * @param folderId - Folder ID
 * @param position - Position of the note in the folder (default: 0)
 * @returns Promise with created folder-note relationship
 */
export const addNoteToFolder = async (
  noteId: number,
  folderId: number,
  position: number = 0
): Promise<{ note: Note, folderNote: FolderNote }> => {
  // First get the note to return it with the response
  const note = await getNoteById(noteId);

  const { data, error } = await supabase
    .from('folder_notes')
    .insert([
      {
        note_id: noteId,
        folder_id: folderId,
        position,
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error adding note to folder:', error);
    throw error;
  }


  return {
    note,
    folderNote: data
  };
};

/**
 * Remove a note from a folder
 * @param noteId - Note ID
 * @param folderId - Folder ID
 */
export const removeNoteFromFolder = async (
  noteId: number,
  folderId: number
): Promise<void> => {
  const { error } = await supabase
    .from('folder_notes')
    .delete()
    .eq('note_id', noteId)
    .eq('folder_id', folderId);

  if (error) {
    console.error('Error removing note from folder:', error);
    throw error;
  }


};

/**
 * Get all notes in a folder
 * @param folderId - Folder ID
 * @returns Promise with array of notes in the folder
 */
export const getFolderNotes = async (folderId: number | null): Promise<Note[]> => {
  // Only fetch necessary fields (id, title) for efficiency
  let query = supabase
    .from('folder_notes')
    .select(`
      id,
      note_id,
      folder_id,
      position,
      created_at,
      note:note_id (id, title, created_at, updated_at, is_pinned)
    `)
    .order('position');

  if (folderId === null) {
      query = query.is('folder_id', null);
  } else {
      query = query.eq('folder_id', folderId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching folder notes:', error);
    throw error;
  }

  // Extract the notes from the relationships and preserve the order
  const notes: Note[] = [];

  for (const item of data) {
    if (item.note && typeof item.note === 'object') {
      // Convert to unknown first, then to Note
      const noteData = item.note as unknown;
      notes.push(noteData as Note);
    } else {
      console.error('Expected note object not found in folder_notes response');
    }
  }

  return notes;
};

/**
 * Update the position of a note within a folder
 * @param noteId - Note ID
 * @param folderId - Folder ID
 * @param newPosition - New position
 * @returns Promise with updated folder-note relationship
 */
export const updateNotePosition = async (
  noteId: number,
  folderId: number,
  newPosition: number
): Promise<FolderNote> => {
  const { data, error } = await supabase
    .from('folder_notes')
    .update({ position: newPosition })
    .eq('note_id', noteId)
    .eq('folder_id', folderId)
    .select()
    .single();

  if (error) {
    console.error('Error updating note position:', error);
    throw error;
  }


  return data;
};

/**
 * Helper to Find Folder and Build Path
 */
export const findFolderInTree = (
    nodes: FolderWithChildren[],
    targetId: number,
    path: FolderWithChildren[] = []
): { folder: FolderWithChildren, path: FolderWithChildren[] } | null => {
    for (const node of nodes) {
        if (node.id === targetId) {
            return { folder: node, path: [...path, node] };
        }
        if (node.children) {
            const result = findFolderInTree(node.children, targetId, [...path, node]);
            if (result) return result;
        }
    }
    return null;
};